import { v4 as uuidv4 } from 'uuid';
import { dequal } from 'dequal';
import {
  Checkpoint,
  InterviewQuestion,
  InterviewQuestions,
  InterviewQuestionsChildren,
  Media,
  ScreeningQuestion,
} from '@/types';
import { GroupContentType } from '@/enums';

type TreeChild = InterviewQuestion | InterviewQuestions | Checkpoint;

/**
 * Type guard to check if a child is a group/subgroup (has children array)
 */
export const isGroup = (item: TreeChild): item is InterviewQuestions => {
  return 'children' in item && Array.isArray(item.children);
};

/**
 * Type guard to check if a child is a question (has question field)
 */
export const isQuestion = (item: TreeChild): item is InterviewQuestion => {
  return 'question' in item;
};

/**
 * TELV1-421 — type guard for a checkpoint node. Primary signal is the literal
 * `type: 'checkpoint'`; the structural test (a `label`, no `question`/`children`)
 * is the defensive secondary. Kept identical to the backend `isCheckpoint`.
 */
export const isCheckpoint = (item: TreeChild): item is Checkpoint => {
  if (!item || typeof item !== 'object') return false;
  if ((item as Checkpoint).type === 'checkpoint') return true;
  return 'label' in item && !('question' in item) && !('children' in item);
};

/**
 * TELV1-457 — split a flat children array into blocks (segments) delimited by
 * checkpoint markers. A checkpoint starts a fresh block; every other node
 * appends to the current block. The leading edge is always a block, so the
 * result length === checkpointCount + 1. Single source of truth shared by the
 * editor (gating + counter) and the validation schema.
 */
export const splitIntoCheckpointBlocks = (children: TreeChild[]): TreeChild[][] =>
  (children || []).reduce<TreeChild[][]>(
    (blocks, child) => {
      if (isCheckpoint(child)) {
        blocks.push([]);
      } else {
        blocks[blocks.length - 1].push(child);
      }
      return blocks;
    },
    [[]],
  );

/**
 * TELV1-457 — count the leaf questions inside a single block. Embed blocks are
 * flat (no groups), but this stays defensive by recursing into any group nodes.
 */
export const countQuestionsInBlock = (block: TreeChild[]): number =>
  (block || []).reduce((count, child) => {
    if (isQuestion(child)) return count + 1;
    if (isGroup(child)) return count + countQuestionsInBlock(child.children as TreeChild[]);
    return count;
  }, 0);

/**
 * Determines if we should render the group UI or flat question list
 * Groups should be visible when:
 * 1. There are multiple root-level groups (children contains multiple groups) OR
 * 2. There is at least one subgroup within the single root group
 */
export const shouldRenderGroupUI = (interviewQuestions: InterviewQuestions): boolean => {
  if (!interviewQuestions?.children || interviewQuestions.children.length === 0) {
    return false;
  }

  const { children } = interviewQuestions;

  // Check if all children are questions (no groups) - initial/flat state
  const allChildrenAreQuestions = children.every(isQuestion);

  if (allChildrenAreQuestions) {
    return false; // Render flat list
  }

  // If any child is a group, we should render group UI
  return children.some(isGroup);
};

/**
 * Gets the content type of a group's children
 * Returns 'questions', 'groups' or 'empty'
 */
export const getGroupContentType = (group: InterviewQuestions): GroupContentType => {
  if (!group.children || group.children.length === 0) {
    return GroupContentType.EMPTY;
  }

  const hasQuestions = group.children.some(isQuestion);
  const hasGroups = group.children.some(isGroup);

  if (hasQuestions) {
    return GroupContentType.QUESTIONS;
  }
  if (hasGroups) {
    return GroupContentType.GROUPS;
  }

  return GroupContentType.EMPTY;
};

// Single source of truth for the auto-generated names assigned to a newly
// created (sub)group by every group-creation call site
// (useDiscussionGuideHandlers.ts, useDragAndDropHandlers.ts). Sharing these
// with `DEFAULT_GROUP_NAME_PATTERN` below — instead of each site hardcoding
// its own literal — guarantees the prune-on-save logic can never drift out
// of sync with what group creation actually names things. TELV1-490 PR
// review MINOR finding.
export const DEFAULT_GROUP_NAME = 'New group';
const GROUP_NAME_PREFIX = 'Group';
const SUBGROUP_NAME_PREFIX = 'Subgroup';
export const getDefaultGroupName = (ordinal: number): string => `${GROUP_NAME_PREFIX} ${ordinal}`;
export const getDefaultSubgroupName = (ordinal: number): string =>
  `${SUBGROUP_NAME_PREFIX} ${ordinal}`;

/**
 * Creates a new empty group structure with a unique ID
 */
export const createEmptyGroup = (name: string = DEFAULT_GROUP_NAME): InterviewQuestions => {
  return {
    id: uuidv4(), // Assign unique ID to avoid conflicts when dragging
    name,
    randomize: false,
    children: [],
  };
};

/**
 * Creates a new empty question
 */
export const createEmptyQuestion = (): InterviewQuestion => {
  return {
    id: uuidv4(),
    question: '',
    follow_up_question: '0',
    answer_quantification: [
      {
        quantify_answer: false,
        value: '',
      },
    ],
  };
};

/**
 * Flattens a group structure into a flat array of questions
 * Used for backward compatibility and when rendering flat view
 */
export const flattenGroupToQuestions = (group: InterviewQuestions): InterviewQuestion[] => {
  const questions: InterviewQuestion[] = [];

  const traverse = (item: TreeChild) => {
    if (isQuestion(item)) {
      questions.push(item);
    } else if (isGroup(item)) {
      item.children.forEach(traverse);
    }
    // TELV1-421 — checkpoints are not questions; skip them (mirrors backend).
  };

  if (group.children) {
    group.children.forEach(traverse);
  }

  return questions;
};

/**
 * Wraps flat questions array into a single group structure
 * Used when transforming frontend data to backend format
 */
export const wrapQuestionsInGroup = (
  questions: InterviewQuestion[],
  groupName: string = 'Interview Questions',
  randomize: boolean = false,
): InterviewQuestions => {
  return {
    id: uuidv4(),
    name: groupName,
    randomize,
    children: questions,
  };
};

/**
 * Finds an item (question or group) and returns its path in the tree
 */
export const findItemPath = (
  itemId: string,
  isQuestionItem: boolean,
  groupStructure: InterviewQuestions,
): number[] | null => {
  const search = (group: InterviewQuestions, path: number[]): number[] | null => {
    for (let i = 0; i < group.children.length; i += 1) {
      const child = group.children[i];

      if (isQuestionItem && isQuestion(child) && child.id === itemId) {
        return [...path, i];
      }

      // For groups, check both ID and name for backward compatibility
      if (
        !isQuestionItem &&
        isGroup(child) &&
        ((child.id && child.id === itemId) || child.name === itemId)
      ) {
        return [...path, i];
      }

      if (isGroup(child)) {
        const result = search(child, [...path, i]);
        if (result) return result;
      }
    }
    return null;
  };

  return search(groupStructure, []);
};

/**
 * Gets a group at a specific path in the tree
 */
export const getGroupAtPath = (
  structure: InterviewQuestions,
  path: number[],
): InterviewQuestions | null => {
  let current = structure;
  for (let i = 0; i < path.length; i += 1) {
    const idx = path[i];
    const child = current.children[idx];
    if (isGroup(child)) {
      current = child;
    } else {
      return null;
    }
  }
  return current;
};

/**
 * Finds a subgroup by ID in the tree
 */
export const findSubgroupById = (
  structure: InterviewQuestions,
  targetId: string,
): InterviewQuestions | null => {
  // Check each child group
  const groups = structure.children.filter(isGroup);

  // First check if any direct child matches
  const directMatch = groups.find((child) => (child.id || child.name) === targetId);
  if (directMatch) return directMatch;

  // Recursively search in child groups
  // eslint-disable-next-line no-restricted-syntax
  for (const child of groups) {
    const found = findSubgroupById(child, targetId);
    if (found) return found;
  }

  return null;
};

/**
 * Removes a question from the tree by ID
 */
export const removeQuestionFromTree = (
  structure: InterviewQuestions,
  questionId: string,
): InterviewQuestions => {
  return {
    ...structure,
    children: structure.children
      .filter((child) => !isQuestion(child) || child.id !== questionId)
      .map((child) => {
        if (isGroup(child)) {
          return removeQuestionFromTree(child, questionId);
        }
        return child;
      }) as InterviewQuestionsChildren,
  };
};

/**
 * Removes a subgroup from the tree by ID
 */
export const removeSubgroupFromTree = (
  structure: InterviewQuestions,
  subgroupId: string,
): InterviewQuestions => {
  return {
    ...structure,
    children: structure.children
      .filter((child) => !isGroup(child) || (child.id || child.name) !== subgroupId)
      .map((child) => {
        if (isGroup(child)) {
          return removeSubgroupFromTree(child, subgroupId);
        }
        return child;
      }) as InterviewQuestionsChildren,
  };
};

/**
 * Checks if any question in the group structure has empty text
 */
export const hasEmptyQuestions = (structure: InterviewQuestions): boolean => {
  const allQuestions = flattenGroupToQuestions(structure);
  return allQuestions.some(({ question }) => !question || question.trim() === '');
};

/**
 * Checks if the interview questions structure is empty (no children or all empty)
 */
export const isInterviewQuestionsEmpty = (structure?: InterviewQuestions): boolean => {
  if (!structure?.children || structure.children.length === 0) {
    return true;
  }

  // Recursively check all children
  return structure.children.every((child) => {
    if (isQuestion(child)) {
      // A question is empty if it has no text or only whitespace
      return !child.question || child.question.trim() === '';
    }
    if (isGroup(child)) {
      // Recursively check if the group is empty
      return isInterviewQuestionsEmpty(child);
    }
    return true;
  });
};

// Matches the auto-generated names assigned by createEmptyGroup /
// handleCreateGroup / handleAddSubgroupToGroup ("New group", "Group 1",
// "Subgroup 2", ...). Built from the same DEFAULT_GROUP_NAME /
// getDefaultGroupName / getDefaultSubgroupName that every group-creation
// call site uses, rather than a hand-maintained duplicate literal, so this
// can't silently fall out of sync with what those sites actually generate
// (verified: all call sites use hardcoded English literals, never a
// translated/localized name, so this pattern does not need to vary by
// locale). A group still carrying one of these names has never been renamed
// by the user.
const DEFAULT_GROUP_NAME_PATTERN = new RegExp(
  `^(${DEFAULT_GROUP_NAME}|${GROUP_NAME_PREFIX} \\d+|${SUBGROUP_NAME_PREFIX} \\d+)$`,
);

const collectGroupIds = (structure: InterviewQuestions | undefined, ids: Set<string>): void => {
  structure?.children?.forEach((child) => {
    if (isGroup(child)) {
      if (child.id) ids.add(child.id);
      collectGroupIds(child, ids);
    }
  });
};

/**
 * A group is a "no-intent" scaffold — safe to silently drop on submit —
 * only when ALL of the following hold: it has no children, it still carries
 * an auto-generated name (the user never renamed it), and it was never
 * persisted to the server (a group that made it to the server was, by
 * definition, saved with content at some point and deserves a visible error
 * instead of silent deletion). A user-named or previously-saved empty group
 * is intentionally left alone — see TELV1-490 architect review Point 2a.
 */
const isPrunableEmptyGroup = (group: InterviewQuestions, serverGroupIds: Set<string>): boolean =>
  (group.children?.length ?? 0) === 0 &&
  DEFAULT_GROUP_NAME_PATTERN.test(group.name.trim()) &&
  !serverGroupIds.has(group.id);

/**
 * Recursively removes untouched, never-saved, auto-scaffolded empty groups
 * from an interview-questions tree. Never touches the root group itself
 * (the root is allowed to be empty by the validation schema) and never
 * removes questions. Returns the original `structure` reference when
 * nothing was pruned, so callers can cheaply skip a `setValue` call.
 */
export const pruneUntouchedEmptyGroups = (
  structure: InterviewQuestions | undefined,
  serverStructure: InterviewQuestions | undefined,
): InterviewQuestions | undefined => {
  if (!structure?.children) return structure;

  const serverGroupIds = new Set<string>();
  collectGroupIds(serverStructure, serverGroupIds);

  const pruneNode = (node: InterviewQuestions): InterviewQuestions => {
    const prunedChildren = node.children
      .map((child) => (isGroup(child) ? pruneNode(child) : child))
      .filter((child) => !isGroup(child) || !isPrunableEmptyGroup(child, serverGroupIds));

    return { ...node, children: prunedChildren as InterviewQuestionsChildren };
  };

  const result = pruneNode(structure);
  return dequal(result, structure) ? structure : result;
};

/**
 * Merge server-owned media membership into a locally-edited interview
 * question tree (TELV1-520 follow-up — the "silent stimulus loss" bug).
 *
 * Media files are uploaded/deleted through their own endpoint, which
 * refetches the project mid-edit. The reset effect preserves the user's
 * unsaved edits by re-applying their captured tree wholesale — a snapshot
 * taken BEFORE the upload/delete, so its media membership is stale, and the
 * next save would overwrite the server's tree with it: a fresh upload
 * silently vanishes (orphaning the stored file), a deletion gets
 * resurrected as a dangling reference (NoSuchKey when served — the
 * TELV1-461 class).
 *
 * Ownership rule this encodes: media MEMBERSHIP belongs to the server
 * (upload/delete endpoints); `label` and ORDER belong to the client until
 * saved (they ride the project-save payload). Per question (matched by id):
 * - items = the server's media list (uploads appear, deletions stay gone)
 * - order = the user's unsaved order for items both sides know, with
 *   server-new items appended in server order
 * - label = the user's unsaved label where it differs, else the server's
 * Questions the server doesn't know yet (added client-side, unsaved) keep
 * their local state untouched; legacy non-array media (`""`) counts as none.
 */
/**
 * Item-level half of the ownership rule, shared by the interview-tree and
 * screening-list merges: given a question and its server counterpart, return
 * the question with server-owned media MEMBERSHIP and client-owned label +
 * order (see mergeServerMediaIntoQuestionTree for the full rationale).
 *
 * No server counterpart means either a client-new question (which can never
 * legitimately hold media — upload is gated on the question existing
 * server-side) or a question another session deleted server-side, whose
 * media's storage objects the delete's orphan cleanup already removed
 * (re-saving them creates dangling NoSuchKey references). Either way media
 * is stripped and the rest of the question passes through.
 */
export const applyServerOwnedMedia = <T extends { media?: Media[] | unknown }>(
  question: T,
  serverQuestion: { media?: Media[] | unknown } | undefined,
): T => {
  if (!serverQuestion) {
    if (!Array.isArray(question.media) || question.media.length === 0) return question;
    const { media: _media, ...rest } = question;
    return rest as T;
  }

  const serverMedia = Array.isArray(serverQuestion.media) ? (serverQuestion.media as Media[]) : [];
  const localMedia = Array.isArray(question.media) ? (question.media as Media[]) : [];

  if (serverMedia.length === 0) {
    // Server has none (never had any, or all deleted). Adopt that — but
    // avoid materializing a media key on a question that never had one.
    if (!('media' in question) && localMedia.length === 0) return question;
    return { ...question, media: [] };
  }

  // Falsy _ids are skipped and orderedIds is deduped: a duplicate or
  // missing _id must never collapse two items into one (which would drop
  // an item from the saved membership and orphan its storage object).
  const serverItemsById = new Map(
    serverMedia.filter((item) => item && item._id).map((item) => [item._id, item]),
  );
  const localLabelsById = new Map(
    localMedia
      .filter((item) => item && typeof item === 'object' && item._id)
      .map((item) => [item._id, item.label]),
  );

  const localOrder = localMedia
    .filter((item) => item && typeof item === 'object' && serverItemsById.has(item._id))
    .map((item) => item._id);
  const orderedIds = [
    ...new Set([
      ...localOrder,
      ...serverMedia.filter((item) => item && item._id).map((item) => item._id),
    ]),
  ];

  const mergedMedia = orderedIds.map((id) => {
    const serverItem = serverItemsById.get(id) as Media;
    const localLabel = localLabelsById.get(id);
    return localLabel !== undefined && localLabel !== serverItem.label
      ? { ...serverItem, label: localLabel }
      : serverItem;
  });

  return { ...question, media: mergedMedia };
};

/**
 * Flat-list counterpart of mergeServerMediaIntoQuestionTree for screening
 * questions — same ownership rule, same use sites (draft restore, and any
 * other path that re-applies a stale captured screening list). TELV1-400
 * architect review F1/F5.
 */
export const mergeServerMediaIntoScreeningQuestions = (
  formList: ScreeningQuestion[] | undefined,
  serverList: ScreeningQuestion[] | undefined,
): ScreeningQuestion[] | undefined => {
  if (!formList) return formList;
  const serverById = new Map(
    (serverList ?? [])
      .filter((question) => question?.id)
      .map((question) => [question.id, question]),
  );
  return formList.map((question) => applyServerOwnedMedia(question, serverById.get(question.id)));
};

export const mergeServerMediaIntoQuestionTree = (
  formTree: InterviewQuestions | undefined,
  serverTree: InterviewQuestions | undefined,
): InterviewQuestions | undefined => {
  if (!formTree) return formTree;

  const serverQuestionsById = new Map<string, InterviewQuestion>();
  const collect = (node: InterviewQuestions | undefined) => {
    (node?.children ?? []).forEach((child) => {
      if (isGroup(child)) collect(child);
      else if (child?.id) serverQuestionsById.set(child.id, child as InterviewQuestion);
    });
  };
  collect(serverTree);

  const mergeQuestion = (question: InterviewQuestion): InterviewQuestion =>
    applyServerOwnedMedia(question, serverQuestionsById.get(question.id));

  const mergeNode = (node: InterviewQuestions): InterviewQuestions => ({
    ...node,
    children: (node.children ?? []).map((child) =>
      isGroup(child) ? mergeNode(child) : mergeQuestion(child as InterviewQuestion),
    ) as InterviewQuestionsChildren,
  });

  return mergeNode(formTree);
};
